import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { LogOut, Mail, Phone, Search, RefreshCw, Inbox } from "lucide-react";
import { toast } from "sonner";
import { useAuth, formatApiError } from "@/lib/auth";

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function AdminLeads() {
  const { user, logout, authAxios } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await authAxios({ method: "GET", url: "/contact" });
      setLeads(data);
    } catch (err) {
      toast.error(formatApiError(err, "Failed to load leads."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    if (!q.trim()) return leads;
    const s = q.toLowerCase();
    return leads.filter(
      (l) =>
        l.name?.toLowerCase().includes(s) ||
        l.email?.toLowerCase().includes(s) ||
        (l.service || "").toLowerCase().includes(s) ||
        (l.phone || "").toLowerCase().includes(s) ||
        (l.message || "").toLowerCase().includes(s)
    );
  }, [q, leads]);

  const onLogout = () => {
    logout();
    toast.success("Signed out.");
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className="min-h-screen flex flex-col bg-zinc-50" data-testid="admin-leads-page">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3" data-testid="admin-leads-logo">
            <img
              src="/logo.png"
              alt="IVrenovation and Construction"
              className="h-9 w-auto object-contain"
            />
            <span className="text-xs uppercase tracking-[0.2em] text-zinc-500 border-l border-zinc-300 pl-3">admin</span>
          </Link>
          <div className="flex items-center gap-3">
            <span className="hidden md:inline text-sm text-zinc-600" data-testid="admin-leads-user-email">
              {user?.email}
            </span>
            <button
              onClick={onLogout}
              data-testid="admin-leads-logout"
              className="flex items-center gap-2 border border-zinc-300 px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-zinc-950 hover:text-white hover:border-zinc-950 transition"
            >
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="flex-1 max-w-7xl w-full mx-auto px-6 md:px-10 py-10">
        {/* Title row */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <p className="overline">Inbox</p>
            <h1 className="font-display text-4xl md:text-5xl tracking-tighter text-zinc-950 mt-2">
              Quote Requests
            </h1>
            <p className="text-zinc-600 mt-2">
              {leads.length} total · showing {filtered.length}
            </p>
          </div>
          <div className="flex items-center gap-3 w-full md:w-auto">
            <div className="relative flex-1 md:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search leads…"
                data-testid="admin-leads-search"
                className="w-full pl-10 pr-4 py-3 border border-zinc-300 bg-white text-sm text-zinc-900 focus:outline-none focus:border-orange-600 transition"
              />
            </div>
            <button
              onClick={load}
              data-testid="admin-leads-refresh"
              className="border border-zinc-300 px-4 py-3 hover:bg-zinc-950 hover:text-white hover:border-zinc-950 transition"
              aria-label="Refresh"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="mt-8 bg-white border border-zinc-200">
          {loading ? (
            <div className="p-16 text-center text-zinc-500" data-testid="admin-leads-loading">
              Loading leads…
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-16 text-center" data-testid="admin-leads-empty">
              <Inbox className="w-10 h-10 mx-auto text-zinc-400" />
              <p className="mt-4 font-display text-2xl text-zinc-900">No leads yet</p>
              <p className="mt-2 text-zinc-500 text-sm">
                {leads.length === 0
                  ? "When customers submit the contact form, they'll appear here."
                  : "No leads match your search."}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left" data-testid="admin-leads-table">
                <thead>
                  <tr className="border-b border-zinc-200 text-xs uppercase tracking-wider text-zinc-500">
                    <th className="px-5 py-4 font-bold">Date</th>
                    <th className="px-5 py-4 font-bold">Name</th>
                    <th className="px-5 py-4 font-bold">Contact</th>
                    <th className="px-5 py-4 font-bold">Service</th>
                    <th className="px-5 py-4 font-bold">Message</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((lead, idx) => (
                    <motion.tr
                      key={lead.id}
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.25, delay: idx * 0.02 }}
                      onClick={() => setSelected(lead)}
                      className="border-b border-zinc-100 hover:bg-zinc-50 transition cursor-pointer"
                      data-testid={`admin-lead-row-${lead.id}`}
                    >
                      <td className="px-5 py-4 text-sm text-zinc-600 whitespace-nowrap">
                        {formatDate(lead.created_at)}
                      </td>
                      <td className="px-5 py-4 font-semibold text-zinc-900">{lead.name}</td>
                      <td className="px-5 py-4 text-sm">
                        <div className="flex flex-col gap-1">
                          <a
                            href={`mailto:${lead.email}`}
                            onClick={(e) => e.stopPropagation()}
                            className="flex items-center gap-2 text-zinc-700 hover:text-orange-600 transition"
                          >
                            <Mail className="w-3.5 h-3.5" /> {lead.email}
                          </a>
                          {lead.phone && (
                            <a
                              href={`tel:${lead.phone}`}
                              onClick={(e) => e.stopPropagation()}
                              className="flex items-center gap-2 text-zinc-500 hover:text-orange-600 transition"
                            >
                              <Phone className="w-3.5 h-3.5" /> {lead.phone}
                            </a>
                          )}
                        </div>
                      </td>
                      <td className="px-5 py-4 text-sm">
                        {lead.service ? (
                          <span className="inline-block bg-orange-50 text-orange-700 border border-orange-200 px-2.5 py-1 text-xs font-bold uppercase tracking-wider">
                            {lead.service}
                          </span>
                        ) : (
                          <span className="text-zinc-400">—</span>
                        )}
                      </td>
                      <td className="px-5 py-4 text-sm text-zinc-700 max-w-md">
                        <p className="line-clamp-2">{lead.message}</p>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Detail drawer */}
      {selected && (
        <div
          className="fixed inset-0 bg-zinc-950/60 backdrop-blur-sm z-50 flex justify-end"
          onClick={() => setSelected(null)}
          data-testid="admin-lead-detail-overlay"
        >
          <motion.aside
            initial={{ x: 40, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.3 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md bg-white h-full overflow-y-auto p-8 border-l border-zinc-200"
            data-testid="admin-lead-detail"
          >
            <button
              onClick={() => setSelected(null)}
              className="text-xs uppercase tracking-wider text-zinc-500 hover:text-orange-600"
              data-testid="admin-lead-detail-close"
            >
              ← Close
            </button>
            <p className="overline mt-6">Lead</p>
            <h2 className="font-display text-3xl tracking-tighter text-zinc-950 mt-2">{selected.name}</h2>
            <p className="text-sm text-zinc-500 mt-2">{formatDate(selected.created_at)}</p>

            <div className="mt-8 space-y-5 text-sm">
              <div>
                <p className="text-xs uppercase tracking-wider text-zinc-500 font-bold">Email</p>
                <a href={`mailto:${selected.email}`} className="text-zinc-900 hover:text-orange-600">{selected.email}</a>
              </div>
              {selected.phone && (
                <div>
                  <p className="text-xs uppercase tracking-wider text-zinc-500 font-bold">Phone</p>
                  <a href={`tel:${selected.phone}`} className="text-zinc-900 hover:text-orange-600">{selected.phone}</a>
                </div>
              )}
              {selected.service && (
                <div>
                  <p className="text-xs uppercase tracking-wider text-zinc-500 font-bold">Service</p>
                  <p className="text-zinc-900">{selected.service}</p>
                </div>
              )}
              <div>
                <p className="text-xs uppercase tracking-wider text-zinc-500 font-bold">Message</p>
                <p className="text-zinc-900 whitespace-pre-wrap leading-relaxed">{selected.message}</p>
              </div>
            </div>

            <a
              href={`mailto:${selected.email}?subject=Re: Your IVrenovation quote request`}
              className="mt-10 inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 text-sm font-bold uppercase tracking-wider transition"
              data-testid="admin-lead-detail-reply"
            >
              <Mail className="w-4 h-4" /> Reply via email
            </a>
          </motion.aside>
        </div>
      )}
    </div>
  );
}
